// src/components/FrontMatterEditor.tsx
import React, { useState, useContext } from 'react';
import { FrontMatter, BookStructureElementKey } from '../types';
import { BOOK_SIZES, getDefaultBookSize } from '@shared/book-size-constants'; 
import { labelClass, textareaClass, inputClass, primaryButtonClass, secondaryButtonClass, actionButtonClass } from '../constants';
import ImageUpload from './ImageUpload';
import { CoverImageWithFallback } from './CoverImageWithFallback';
import TableOfContentsGenerator from './TableOfContentsGenerator';
import { AppContext, AppContextType } from '../contexts/AppContext';
import { marked } from 'marked';
import { useTOCGeneration, useTOCStatus } from '../hooks/useTOCGeneration';

interface FrontMatterEditorProps {
    frontMatter: FrontMatter;
    onFrontMatterChange: <K extends keyof FrontMatter>(field: K, value: FrontMatter[K]) => void;
}

const FrontMatterEditor: React.FC<FrontMatterEditorProps> = ({ frontMatter, onFrontMatterChange }) => {
    const appContext = useContext(AppContext);
    const isLoadingAI = appContext?.isLoading['generateFrontMatterElement'] || appContext?.isLoading['generateTableOfContents'];
    const isLoadingTitleImageAI = appContext?.isLoading['generateTitlePageImage'];
    const [previewContent, setPreviewContent] = useState<string | null>(null);
    const [previewTitle, setPreviewTitle] = useState<string | null>(null);
    const [showTOCGenerator, setShowTOCGenerator] = useState(false);

    // Auto-TOC Generation Hook
    const tocGeneration = useTOCGeneration(
        appContext?.projectState || {} as any,
        (updates) => {
            if (updates.frontMatter?.tableOfContents) {
                onFrontMatterChange('tableOfContents', updates.frontMatter.tableOfContents);
            }
        },
        {
            autoTriggerEnabled: true,
            preferredStyle: 'standard',
            minChapters: 2
        }
    );

    const tocStatus = useTOCStatus(appContext?.projectState || {} as any);
    
    const fields: Array<{ key: keyof Omit<FrontMatter, 'titlePageImageBase64' | 'titleImagePrompt' | 'titleImageAspectRatio' | 'titleImageBase64'>; label: string; placeholder: string; type?: 'input' | 'textarea', rows?: number, aiGenType?: BookStructureElementKey }> = [
        { key: 'titlePageContent', label: 'Title Page (Internal Content)', placeholder: 'Enter internal title page content (e.g., Title, Author, Publisher). Markdown supported.', type: 'textarea', rows: 3 },
        { key: 'copyrightPageContent', label: 'Copyright Page', placeholder: 'Enter copyright details (e.g., © Year by Author/Publisher, ISBN, Disclaimers). Markdown supported.', type: 'textarea', rows: 5 },
        { key: 'dedication', label: 'Dedication', placeholder: 'Enter dedication text. Markdown supported.', type: 'textarea', rows: 2, aiGenType: 'dedication' },
        { key: 'epigraph', label: 'Epigraph', placeholder: 'Enter epigraph (quote, poem). Markdown supported.', type: 'textarea', rows: 2, aiGenType: 'epigraph' },
        { key: 'tableOfContents', label: 'Table of Contents', placeholder: 'Manually enter, paste, or generate Table of Contents. Markdown supported for formatting.', type: 'textarea', rows: 8, aiGenType: 'tableOfContents' },
        { key: 'foreword', label: 'Foreword', placeholder: 'Enter foreword content. Markdown supported.', type: 'textarea', rows: 5 }, 
        { key: 'forewordBy', label: 'Foreword By', placeholder: 'Name of the person who wrote the foreword.', type: 'input' },
        { key: 'preface', label: 'Preface', placeholder: 'Enter preface content. Markdown supported.', type: 'textarea', rows: 5, aiGenType: 'preface' },
        { key: 'acknowledgmentsFront', label: 'Acknowledgments (Front)', placeholder: 'Enter acknowledgments if placed in front matter. Markdown supported.', type: 'textarea', rows: 4, aiGenType: 'acknowledgmentsFront' },
        { key: 'prologue', label: 'Prologue (Fiction)', placeholder: 'Enter prologue content. Markdown supported.', type: 'textarea', rows: 8, aiGenType: 'prologue' },
        { key: 'introduction', label: 'Introduction (Non-Fiction)', placeholder: 'Enter introduction content. Markdown supported.', type: 'textarea', rows: 8, aiGenType: 'introduction' },
    ];

    const handleImageUpload = (base64Image: string | null) => {
        onFrontMatterChange('titlePageImageBase64', base64Image);
    };

    const showPreview = (title: string, contentKey: keyof FrontMatter) => {
        setPreviewTitle(title);
        const mdContent = frontMatter[contentKey] as string || '';
        try {
            const result = marked(mdContent);
            if (result instanceof Promise) {
                result.then(setPreviewContent).catch(() => setPreviewContent(mdContent));
            } else {
                setPreviewContent(result);
            }
        } catch (error) {
            setPreviewContent(mdContent); // Fallback to original content
        }
    };

    const handleGenerateElementWithAI = (elementType: BookStructureElementKey) => {
        appContext?.handleGenerateIndividualBookStructureTextElement(elementType, true);
    };

    const handleTOCApply = (updatedProject: any) => {
        if (updatedProject.frontMatter?.tableOfContents) {
            onFrontMatterChange('tableOfContents', updatedProject.frontMatter.tableOfContents);
        }
    };
    
    const handleGenerateTitlePageImage = () => {
        if (!appContext) return;
        const { projectState } = appContext;
        if (!projectState) return;
        appContext.openImageGenerationModalFromContext('titlePage', {
            bookTitle: projectState.blueprint?.worldBuildingSuggestions?.selectedStoryTitle || "My Book",
            genre: projectState.bookMetadata?.genreSubject || "General",
            themes: projectState.blueprint?.worldBuildingSuggestions?.themes || [],
            authorName: projectState.authorDetails?.authorName,
            overallMood: projectState.bookMetadata?.toneAndMood || 'Intriguing',
            logLine: projectState.blueprint?.worldBuildingSuggestions?.logLine || '',
        });
    };

    const handleGenerateTitleImage = () => {

        if (!appContext) return;

        const { projectState } = appContext;

        appContext.openImageGenerationModalFromContext('titlePage', frontMatter.titleImagePrompt || '');
    };

    return (
        <>
            <div className="space-y-6 p-1 h-full flex gap-4">
                <div className="w-2/3 overflow-y-auto pr-2 space-y-6">
                    <h3 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-4">Front Matter Elements</h3>
                    <p className="text-sm text-gray-500 -mt-2 mb-4">Manage the content that appears before your main story. Text fields support Markdown.</p>
                    
                    <div className="md:col-span-2 p-4 border rounded-md bg-gray-50 shadow-sm">
                         <ImageUpload
                            label="Title Page Image (Optional)"
                            currentImage={frontMatter.titlePageImageBase64}
                            onImageUpload={handleImageUpload}
                            recommendedSize="e.g., Cover aspect ratio"
                        />
                        <button
                            onClick={handleGenerateTitlePageImage}
                            disabled={isLoadingTitleImageAI || !appContext?.apiKey}
                            className={`${actionButtonClass} text-xs mt-2`}
                            title={!appContext?.apiKey ? "API Key required" : "Generate Title Page Image using AI"}
                        >
                            {isLoadingTitleImageAI ? 'Generating Image...' : 'Generate Title Page Image (AI)'}
                        </button>
                    </div>

                    {/* Title Image Generation Section */}
                    <div className="md:col-span-2 p-4 border rounded-md bg-blue-50 shadow-sm">
                        <h4 className="text-lg font-medium text-gray-800 mb-3">🎨 Title Image Generation</h4>
                        <p className="text-sm text-gray-600 mb-4">Generate a custom title image for your book with AI.</p>
                        
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="titleImagePrompt" className={`${labelClass} mb-1.5`}>Image Description:</label>
                                <textarea
                                    id="titleImagePrompt"
                                    value={frontMatter.titleImagePrompt || ''}
                                    onChange={(e) => onFrontMatterChange('titleImagePrompt', e.target.value)}
                                    placeholder="Describe the title image you want (e.g., 'A mystical forest with glowing trees and a castle in the distance')"
                                    className={`${textareaClass} min-h-[4rem]`}
                                    rows={3}
                                />
                            </div>
                            
                            <div>
                                <label htmlFor="titleImageAspectRatio" className={`${labelClass} mb-1.5`}>Book Size & Format:</label>
                                <select
                                    id="titleImageAspectRatio"
                                    value={frontMatter.titleImageAspectRatio || getDefaultBookSize().ratio}
                                    onChange={(e) => onFrontMatterChange('titleImageAspectRatio', e.target.value)}
                                    className={inputClass}
                                >
                                    {BOOK_SIZES.map(category => (
                                        <optgroup key={category.category} label={category.label}>
                                            {category.sizes.map(size => (
                                                <option key={size.key} value={size.ratio}>
                                                    {size.label} - {size.description}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Select a realistic book size for professional title image generation. Dimensions shown are width × height.</p>
                            </div>

                            {frontMatter.titleImageBase64 && (
                                <div>
                                    <label className={`${labelClass} mb-2`}>Generated Title Image:</label>
                                    <CoverImageWithFallback
                                        src={frontMatter.titleImageBase64}
                                        alt="Generated Title Image"
                                        className="w-full max-w-md rounded-lg border border-gray-300"
                                    />
                                </div>
                            )}

                            <div className="flex gap-2">
                                <button
                                    onClick={handleGenerateTitleImage}
                                    disabled={!frontMatter.titleImagePrompt?.trim()}
                                    className={`${actionButtonClass} flex-1`}
                                    title="Generate title image with AI based on your description"
                                >
                                    Generate Title Image (AI)
                                </button>
                                {frontMatter.titleImageBase64 && (
                                    <button
                                        onClick={() => onFrontMatterChange('titleImageBase64', null)}
                                        className={`${secondaryButtonClass}`}
                                        title="Remove generated image"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {fields.map(({ key, label, placeholder, type = 'textarea', rows = 3, aiGenType }) => (
                        <div key={key} className="p-4 border rounded-md bg-gray-50 shadow-sm">
                            <label htmlFor={`fm-${String(key)}`} className={`${labelClass} mb-1.5 text-lg font-medium`}>{label}:</label>
                            {type === 'textarea' ? (
                                 <textarea
                                    id={`fm-${String(key)}`}
                                    value={String(frontMatter[key as keyof FrontMatter] || '')}
                                    onChange={(e) => onFrontMatterChange(key as (string & keyof FrontMatter), e.target.value as any)}
                                    placeholder={placeholder}
                                    className={`${textareaClass} min-h-[${Math.max(rows * 1.2, 4)}rem]`}
                                    rows={rows}
                                />
                            ) : (
                                <input
                                    id={`fm-${String(key)}`}
                                    type="text"
                                    value={String(frontMatter[key as keyof FrontMatter] || '')}
                                    onChange={(e) => onFrontMatterChange(key as (string & keyof FrontMatter), e.target.value as any)}
                                    placeholder={placeholder}
                                    className={inputClass}
                                />
                            )}
                            <div className="flex justify-between items-center mt-2">
                                <div className="flex items-center space-x-2">
                                    <p className="text-xs text-gray-500">Supports Markdown.</p>
                                    {key === 'tableOfContents' && (
                                        <div className="flex items-center space-x-1">
                                            {tocStatus.status === 'complete' && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    ✓ Complete
                                                </span>
                                            )}
                                            {tocStatus.status === 'missing-toc' && tocStatus.hasEnoughChapters && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    ⚡ Auto-gen ready
                                                </span>
                                            )}
                                            {tocStatus.status === 'insufficient-chapters' && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                    📝 Need more chapters
                                                </span>
                                            )}
                                            {tocGeneration.tocState.autoGenerated && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    🤖 Auto-generated
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    {aiGenType && (
                                        <button
                                            onClick={() => handleGenerateElementWithAI(aiGenType)}
                                            disabled={(aiGenType === 'tableOfContents' ? appContext?.isLoading['generateTableOfContents'] : isLoadingAI) || !appContext?.apiKey}
                                            className={`${secondaryButtonClass} text-xs mr-2`}
                                            title={!appContext?.apiKey ? "API Key required" : `Generate ${label} with AI`}
                                        >
                                            {(aiGenType === 'tableOfContents' ? appContext?.isLoading['generateTableOfContents'] : isLoadingAI) ? `Generating...` : `Generate (AI)`}
                                        </button>
                                    )}
                                    {key === 'tableOfContents' && (
                                        <>
                                            <button
                                                onClick={() => setShowTOCGenerator(true)}
                                                className={`${actionButtonClass} text-xs mr-2`}
                                                title="Open advanced TOC generator with multiple styles and options"
                                            >
                                                Smart TOC
                                            </button>
                                            {tocStatus.hasEnoughChapters && (
                                                <button
                                                    onClick={() => tocGeneration.regenerateTOC()}
                                                    disabled={tocGeneration.tocState.isGenerating}
                                                    className={`${primaryButtonClass} text-xs mr-2 ${tocGeneration.tocState.isGenerating ? 'opacity-50' : ''}`}
                                                    title="Quickly regenerate TOC from chapters"
                                                >
                                                    {tocGeneration.tocState.isGenerating ? 'Auto-Generating...' : 'Auto TOC'}
                                                </button>
                                            )}
                                        </>
                                    )}
                                    <button 
                                        onClick={() => showPreview(label, key as keyof FrontMatter)}
                                        className={`${primaryButtonClass} text-xs`}
                                    >
                                        Preview
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="w-1/3 p-4 border-l border-gray-200 overflow-y-auto bg-white rounded-r-lg shadow-inner">
                    <h3 className="text-lg font-semibold text-gray-700 mb-3 sticky top-0 bg-white pb-2 border-b">
                        {previewTitle ? `Preview: ${previewTitle}` : "Content Preview"}
                    </h3>
                    {previewContent ? (
                        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewContent }} />
                    ) : (
                        <p className="text-gray-500 text-sm">Click a "Preview" button on an element to see its rendered Markdown here.</p>
                    )}
                </div>
            </div>

            {/* Table of Contents Generator Modal */}
            {appContext?.projectState && (
                <TableOfContentsGenerator
                    projectState={appContext.projectState}
                    onTOCApply={handleTOCApply}
                    isOpen={showTOCGenerator}
                    onClose={() => setShowTOCGenerator(false)}
                />
            )}
        </>
    );
};

export default FrontMatterEditor;